import { PercentIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { NumericInputFormField, PhoneNumberFormField, SelectFormField } from '@/components/form'
import { ChipSelectFormField } from '@/components/form/chip-select-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { FieldValue } from '@/components/layout/field-row'
import { SelectOption } from '@/components/ui/select'
import { TextInput } from '@/components/ui/text-input'
import { Building } from '@/reducers/types'

import { isCanadaCountry } from '../../../places.utils'
import { GeneralPhotoField } from './general-photo-field'
import { GeneralStateField } from './general-state-field'
import {
  CONVENIENCE_FEE_RATE_MAX,
  EMERGENCY_PHONE_MAX_LENGTH,
  PLACE_GENERAL_NUMERIC_ROWS,
  PLACE_GENERAL_PHOTO_ROWS,
  PLACE_TEXT_MAX_LENGTH,
  PlaceGeneralFormData,
  PlaceGeneralRowId,
  PlaceGeneralTextRow,
} from './general-tab.constants'

interface GeneralControlProps {
  building: Building
  countryLabel: string
  row: PlaceGeneralRowId
  tagOptions: SelectOption<string>[]
  timezoneOptions: SelectOption<string>[]
}

export const GeneralControl = ({
  building,
  countryLabel,
  row,
  tagOptions,
  timezoneOptions,
}: GeneralControlProps) => {
  const { t } = useTranslation(['organisation'])

  if (row === 'country') {
    return <TextInput disabled readOnly name="country" size="small" value={countryLabel} />
  }
  if (row === 'timezone') {
    return (
      <SelectFormField<PlaceGeneralFormData, string>
        disabled={!!building.timezone}
        name="timezone"
        options={timezoneOptions}
        placeholder={t('organisation:places.building.timezone.placeholder')}
        size="small"
        type="single"
      />
    )
  }
  if (row === 'state') return <GeneralStateField building={building} />
  if (row === 'phone') {
    return (
      <PhoneNumberFormField
        name="phone"
        placeholder={t('organisation:places.building.fields.phone.placeholder')}
      />
    )
  }
  if (row === 'tags') {
    return (
      <FieldValue>
        <ChipSelectFormField<PlaceGeneralFormData>
          chipColor="blue"
          name="tags"
          options={tagOptions}
          placeholder={t('organisation:places.building.fields.tags.placeholder')}
          size="small"
          onCreate={(label) => label}
        />
      </FieldValue>
    )
  }
  if (PLACE_GENERAL_PHOTO_ROWS.includes(row)) {
    return <GeneralPhotoField building={building} name={row as 'image' | 'logo'} />
  }
  if (row === 'residentConvenienceFeeRate') {
    return (
      <NumericInputFormField
        clearable
        autoComplete="off"
        icon={PercentIcon}
        max={CONVENIENCE_FEE_RATE_MAX}
        mode="decimal"
        name={row}
        placeholder={t(
          'organisation:places.building.fields.residentConvenienceFeeRate.placeholder',
        )}
        size="small"
      />
    )
  }

  const textRow = row as PlaceGeneralTextRow

  if (PLACE_GENERAL_NUMERIC_ROWS.includes(textRow)) {
    return (
      <NumericInputFormField
        clearable
        autoComplete="off"
        maxLength={textRow === 'emergencyPhone' ? EMERGENCY_PHONE_MAX_LENGTH : undefined}
        name={textRow}
        placeholder={t(`organisation:places.building.fields.${textRow}.placeholder`)}
        size="small"
      />
    )
  }

  return (
    <TextInputFormField
      clearable
      withRestore
      autoComplete="off"
      maxLength={textRow === 'postalCode' ? undefined : PLACE_TEXT_MAX_LENGTH}
      name={textRow}
      placeholder={
        textRow === 'postalCode' && isCanadaCountry(building.country)
          ? t('organisation:places.building.fields.postalCode.placeholderCA')
          : t(`organisation:places.building.fields.${textRow}.placeholder`)
      }
      size="small"
    />
  )
}
